<template>
  <div>
    <ol v-if="!showUserSettings" class="breadcrumb mb-2">
      <li class="breadcrumb-item">
        <a href="/" class="router-link-active" target="_self"><svg xmlns="http://www.w3.org/2000/svg" width="16px"
            height="16px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" class="align-text-top feather feather-home">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg></a>
      </li>
      <li class="breadcrumb-item active">
        <span aria-current="location"> {{ $t("Settings") }} </span>
      </li>
      <li class="breadcrumb-item active">
        <span aria-current="location"> {{ $t("Users") }} </span>
      </li>
    </ol>
    <div v-if="!showUserSettings" class="pb-2">
      <div class="custom-search d-flex justify-content-end align-items-center">
        <div v-if="canViewUsers" class="d-flex align-items-center">
          <b-form-checkbox id="inactive-user-switch" v-model="includeInactive" class="colorful-switch" switch />
          <label for="inactive-user-switch">
            <h6 class="cursor-pointer mb-0">
              {{ $t("Include Inactive Users") }}
            </h6>
          </label>
        </div>
      </div>

      <b-row class="justify-content-between align-items-center mt-2 mb-1">
        <b-col id="tour-invite-users" md="6" class="d-flex align-items-baseline mb-1">
          <h3>
            {{ $t("Users") }}
          </h3>
          <b-button v-if="canInviteUser" id="invite-user-btn" v-b-modal.invite-user-modal variant="outline-secondary"
            class="ml-2">
            <feather-icon icon="MailIcon" />
            {{ $t("Invite User By Email") }}
          </b-button>
        </b-col>

        <b-col class="d-flex align-items-baseline justify-content-end" md="6">
          <!-- Search Users -->
          <b-form-group :class="{ 'w-100': IS_MOBILE() }">
            <b-form-input v-model="searchTerm" :placeholder="$t('Search Users')" type="text" class="d-inline-block"
              @input="getUsers" />
          </b-form-group>

          <!-- Filter by Tags -->
          <filter-dropdown
            class="ml-1"
            :include-filters="['isOnline', 'tags']"
          />

          <!-- Manage Permissions -->
          <manage-permissions v-if="canManagePermissions" :permissions="permissions" class="ml-1" />
        </b-col>
      </b-row>

      <!-- Active Filters -->
      <active-filters />

      <b-overlay :show="isProcessing" class="users-list">
        <b-card body-class="p-0">
          <vue-good-table id="users-list-table" :columns="columns" :rows="rows" :row-style-class="getRowStyle"
            @on-cell-click="onCellClick" :sort-options="{ enabled: false }">
            <template slot="table-column" slot-scope="props">
              <div v-if="props.column.label === 'Name'" class="d-flex">
                <span>
                  {{ $t('Name') }}
                </span>
                <table-header-sort
                  :sort-label="'NAME'"
                  :sort-options="sortOptions"
                  @update:sort-options="sortOptions = $event"
                />
              </div>
            </template>
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'id'">
                {{ (currentPage - 1) * 10 + props.row.originalIndex + 1 }}.
              </span>
              <span v-else-if="props.column.field === 'name'">
                <user-with-tags :user="props.row" />
              </span>
              <span v-else-if="props.column.field === 'role'">
                {{ props.row.role }}
              </span>
              <span v-else-if="props.column.field === 'projectRole'">
                {{ props.row.projectRole }}
              </span>
              <span v-else-if="props.column.field === 'settings'">
                <div class="d-flex justify-content-center">
                  <b-dropdown variant="link" toggle-class="text-decoration-none p-0" no-caret right>
                    <!-- "More" Icon -->
                    <template #button-content>
                      <feather-icon icon="MoreVerticalIcon" size="24" />
                    </template>
                    <!-- Edit Wallet Alias -->
                    <b-dropdown-item @click.stop="openUserTags(props.row)" :style="{ cursor: 'pointer' }">
                      <i class="fa fa-tag mr-1" />
                      {{ $t('Manage Tags') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click.stop="viewUserSettings(props.row)" :style="{ cursor: 'pointer' }">
                      <i class="fa fa-gear mr-1" />
                      {{ $t('Advanced Setting') }}
                    </b-dropdown-item>

                  </b-dropdown>

                </div>
              </span>
            </template>

            <template slot="emptystate">
              <p class="text-center">
                {{ $t("No Users Found") }}
              </p>
            </template>
          </vue-good-table>
        </b-card>
      </b-overlay>

      <div class="w-100 centralize">
        <b-pagination v-if="totalItems > perPage" v-model="currentPage" class="pb-2" :total-rows="totalItems"
          :per-page="perPage" aria-controls="users-list-table" @input="
            () => {
              isProcessing = true;
              getUsers();
            }
          " />
      </div>
    </div>

    <invite-user v-if="canInviteUser" />
    <profile-modal v-if="selectedUser" :user-info="selectedUser" @close="selectedUser = null" />

    <!-- User Settings -->
    <user-settings v-if="showUserSettings" :user="userForUserSettings" :can-view-users="canViewUsers"
      :permissions="userSettingsPermissions" @hide="() => (showUserSettings = false)" @refetch-users="getUsers" />
    <b-modal ref="manageTagsModal" size="lg" hide-footer
      :title="manageTagsFor ? `${$t('Manage Tags for')} ${manageTagsFor.name}` : $t('Manage Tags')">
      <assign-user-tags v-if="manageTagsFor" :user="manageTagsFor"
        @saved="() => { if ($refs.manageTagsModal) $refs.manageTagsModal.hide(); getUsers(); }"
        @cancel="() => { if ($refs.manageTagsModal) $refs.manageTagsModal.hide(); }" />
    </b-modal>
  </div>
</template>
<script>
import {
  BAvatar,
  BFormGroup,
  BFormInput,
  BButton,
  BCard,
  BOverlay,
  BPagination,
  BFormCheckbox,
  BRow,
  BCol,
  BDropdown,
  BDropdownItem,
  BModal,
  BBadge,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import EventBus from "@/event-bus";
import useApollo from "@/plugins/graphql/useApollo";
import { checkPermission } from "@/utils/permissions";
import ManagePermissions from "@/views/common/components/ManagePermissions.vue";
import { setComponentReady } from "@/views/tour/tour";
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import InviteUser from "./InviteUser.vue";
import ProfileModal from "./profile.vue";
import UserSettings from "./Settings/Index.vue";
import UserTags from "@/views/Settings/UserTags/index.vue";
import TagBadge from "@/views/Settings/UserTags/Components/TagBadge.vue";
import AssignUserTags from "./AssignUserTags.vue";
import FilterDropdown from "@/views/common/components/Filters/FilterDropdown.vue";
import ActiveFilters from '@/views/common/components/Filters/ActiveFilters.vue'
import TableHeaderSort from "@/views/common/components/TableHeaderSort.vue";
import useFilters from '@/views/common/components/Filters/useFilters'
import UserWithTags from "@/views/common/components/UserWithTags.vue";

export default {
  components: {
    VueGoodTable,
    BAvatar,
    BFormGroup,
    BFormInput,
    BButton,
    BDropdown,
    BDropdownItem,
    BModal,
    BBadge,
    BOverlay,
    BPagination,
    BFormCheckbox,
    BCard,
    BRow,
    BCol,
    InviteUser,
    ProfileModal,
    UserSettings,
    UserTags,
    TagBadge,
    UserWithTags,
    // per-user tag assigner
    AssignUserTags,
    ManagePermissions,
    FilterDropdown,
    ActiveFilters,
    TableHeaderSort,
  },
  data() {
    return {
      searchTerm: "",
      rows: [],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      selectedUser: null,
      manageTagsFor: null,
      showUserSettings: false,
      userForUserSettings: "",
      includeInactive: false,
      isProcessing: true,
      permissions: [
        "projects.invite",
        "project.viewusers",
        "project.manageusers",
        "timing.limit",
        "project.usersettings",
      ],
      needPermissionCheck: false,
      sortOptions: {
        sortBy: 'NAME',
        sortDirection: 'ASC'
      }
    };
  },
  computed: {
    ...mapGetters("permissions", [
      "canManagePermissions",
      "canInviteUser",
      "canViewUsers",
      "canManageUsers",
      "canEditTimeLimits",
      "canEditUserSettings",
    ]),
    userSettingsPermissions() {
      return {
        canEditTimingLimit: this.canEditTimeLimits,
        canEditUserSettings: this.canEditUserSettings,
        canManageUsers: this.canManageUsers,
      };
    },
    canAccessUserSettings() {
      return Object.values(this.userSettingsPermissions).some(
        (permission) => permission === true
      );
    },
    requireUserPermissionCheck() {
      return !(
        this.canInviteUser ||
        this.canViewUsers ||
        this.canEditTimeLimits ||
        this.canEditUserSettings
      );
    },
    columns() {
      return [
        { label: this.$t("S.No."), field: "id" },
        {
          label: this.$t("Name"),
          field: "name",
          tdClass: "cursor-pointer name-col",
        },
        { label: this.$t("Email"), field: "email" },
        { label: this.$t("Company Role"), field: "role" },
        { label: this.$t("Project Role"), field: "projectRole" },
        ...(this.canAccessUserSettings
          ? [
            {
              label: this.$t("User Settings"),
              field: "settings",
              sortable: false,
              tdClass: "text-center",
            },
          ]
          : []),
      ];
    },
    activeFilters() {
      const { getActiveFilters } = useFilters()
      return getActiveFilters.value
    }
  },
  watch: {
    includeInactive() {
      this.getUsers();
    },
    sortOptions: {
      handler() {
        this.isProcessing = true;
        this.getUsers();
      },
      deep: true,
    },
    activeFilters: {
      handler() {
        this.isProcessing = true;
        this.getUsers();
      },
      deep: true,
    }
  },
  mounted() {
    this.getUsers();
    this.checkUserPermissions();
    EventBus.$on("permission-updated", (permission) => {
      if (this.permissions.indexOf(permission) !== -1) {
        this.needPermissionCheck = true;
        setTimeout(() => {
          this.checkUserPermissions();
          if (permission === "project.viewusers") this.getUsers();
        }, 2000);
      }
    });
  },
  beforeDestroy() {
    EventBus.$off("permission-updated");
  },
  methods: {
    ...mapActions("permissions", ["setPermissions"]),
    ...mapActions("project", ["updateUsers"]),
    checkUserPermissions() {
      if (this.requireUserPermissionCheck || this.needPermissionCheck) {
        checkPermission(this.permissions, (permissions) => {
          const canInviteUser = permissions["projects.invite"] === true;
          const canViewUsers = permissions["project.viewusers"] === true;
          const canEditTimingLimit = permissions["timing.limit"] === true;
          const canEditUserSettings =
            permissions["project.usersettings"] === true;
          const canManageUsers = permissions["project.manageusers"] === true;

          const payload = [
            { key: "setUserInvitePermission", value: canInviteUser },
            { key: "setUserViewPermission", value: canViewUsers },
            { key: "setTimeLimitsPermission", value: canEditTimingLimit },
            {
              key: "setUserSettingsEditPermission",
              value: canEditUserSettings,
            },
            { key: "setManageUserPermission", value: canManageUsers },
          ];
          this.setPermissions(payload);
          this.needPermissionCheck = false;
        });
      }
    },
    getRowStyle(row) {
      return row.projectRoles.data[0]?.deactivatedAt ? "bg-light-danger" : "";
    },
    getUsers: _.debounce(function () {
      this.isProcessing = true;
      const companyId = this.$store.state.project.selectedCompany;
      const projectId = this.$store.state.project.selectedProject;
      const { sortBy, sortDirection } = this.sortOptions;
      useApollo.auth
        .listUsers({
          keyword: this.searchTerm,
          page: this.currentPage,
          first: this.perPage,
          companyId,
          projectId,
          includeInactive: this.includeInactive,
          tags: this.activeFilters.tags.map(tag => tag.name),
          sortBy,
          sortDirection,
          ...(this.activeFilters.isOnline !== null && { isOnline: this.activeFilters.isOnline }),
        })
        .then((response) => {
          const { data } = response;
          this.rows = data.users.data.map((user) => ({
            ...user,
            avatar: user.avatar,
            role: user.companyRoles?.data[0]?.role?.displayName,
            projectRole: user.projectRoles?.data[0]?.role?.displayName,
          }));
          this.totalItems = data.users.total;
          this.updateUsers(this.rows);
        })
        .finally(() => {
          setComponentReady();
          this.isProcessing = false;
        });
    }, 1000),
    viewUserSettings(user) {
      this.userForUserSettings = user;
      this.$nextTick(() => {
        this.showUserSettings = true;
      });
    },
    openUserTags(user) {
      // show the Manage Tags modal with per-user tag assignment
      this.manageTagsFor = user;
      this.$nextTick(() => {
        if (this.$refs.manageTagsModal) this.$refs.manageTagsModal.show();
      });
    },
    onCellClick(props) {
      if (props.column.field === "name") this.selectedUser = props.row;
    },
  },
};
</script>

<style lang="scss">
.name-col {
  min-width: 280px;
}
</style>
